import React, { useCallback, useState } from "react";
import { StyleSheet, View } from "react-native";
import { WarningCircleIcon } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";

import { MeDocument, useAcceptAiDisclaimerMutation } from "@axio/graphql/generated/graphql";
import CustomButton from "../../components/common/general/CustomButton/CustomButton";
import CustomModal from "../../components/common/general/CustomModal/CustomModal";
import CustomText from "../../components/common/general/CustomText/CustomText";
import { useAuth } from "../../hooks/useAuth";
import { colors, radii, semanticColors, spacing } from "../../styles/design-system";

interface CompanionAiDisclaimerGateProps {
  children: React.ReactNode;
}

function CompanionAiDisclaimerGate({ children }: CompanionAiDisclaimerGateProps) {
  const { loadingMe, meData } = useAuth();
  const { t } = useTranslation();
  const [acceptAiDisclaimer, { loading: isAccepting }] = useAcceptAiDisclaimerMutation({
    refetchQueries: [MeDocument],
  });
  const [acceptedThisSession, setAcceptedThisSession] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const hasAcceptedAiDisclaimer = meData?.me?.hasAcceptedAiDisclaimer === true;

  const handleAccept = useCallback(async () => {
    setErrorMessage(null);

    try {
      const { data } = await acceptAiDisclaimer();

      if (data?.acceptAiDisclaimer.hasAcceptedAiDisclaimer !== true) {
        throw new Error("AI disclaimer acceptance was not saved.");
      }

      setAcceptedThisSession(true);
    } catch {
      setErrorMessage(t("text.ai_disclaimer_acceptance_error"));
    }
  }, [acceptAiDisclaimer, t]);

  if (loadingMe) {
    return null;
  }

  if (!meData?.me || hasAcceptedAiDisclaimer || acceptedThisSession) {
    return <>{children}</>;
  }

  return (
    <CustomModal
      visible
      onRequestClose={() => {}}
      ariaLabel={t("text.ai_disclaimer_acceptance_title")}
      style={styles.modal}
      bodyStyle={styles.body}
      testID="companion-ai-disclaimer-modal">
      <View style={styles.content}>
        <View style={styles.iconWrap}>
          <WarningCircleIcon size={28} weight="fill" color={colors.blue[600]} aria-hidden />
        </View>
        <CustomText text="ai_disclaimer_acceptance_title" size="xxl" weight="ultraBold" style={styles.title} />
        <CustomText text="ai_tutor_chat_disclaimer" size="m" style={styles.message} />
        {errorMessage && <CustomText text={errorMessage} useTranslationText={false} size="s" style={styles.error} />}
        <CustomButton
          text={t("text.ai_disclaimer_acceptance_accept")}
          useTranslationText={false}
          onPress={handleAccept}
          disabled={isAccepting}
          styleType="primary"
          style={styles.acceptButton}
          aria-label={t("text.ai_disclaimer_acceptance_accept")}
        />
      </View>
    </CustomModal>
  );
}

const styles = StyleSheet.create({
  modal: {
    backgroundColor: colors.white,
    borderRadius: radii.xl,
    maxWidth: 420,
    padding: spacing.xl,
    width: "100%",
  },
  body: {
    marginTop: 0,
  },
  content: {
    alignItems: "center",
    gap: spacing.md,
  },
  iconWrap: {
    alignItems: "center",
    backgroundColor: colors.blue[50],
    borderRadius: radii.full,
    height: 48,
    justifyContent: "center",
    width: 48,
  },
  title: {
    color: semanticColors.typography.displayPrimary,
    textAlign: "center",
  },
  message: {
    color: semanticColors.typography.textPrimary,
    textAlign: "center",
  },
  error: {
    color: colors.error[600],
    textAlign: "center",
  },
  acceptButton: {
    alignSelf: "stretch",
    marginTop: spacing.sm,
  },
});

export default CompanionAiDisclaimerGate;
